import map from 'lodash/map';
import filter from 'lodash/filter';
import each from 'lodash/each';
import find from 'lodash/find';
import get from 'lodash/get';

import {
  LINK_TYPE_INTERNAL
} from 'zoomforth_codesbane_viewer/const/link_types.js';

import {
  SECTION_TYPE_TABS
} from 'zoomforth_codesbane_viewer/const/sections.js';

import loglevel from 'loglevel';

import { getIdFrom } from 'zoomforth_codesbane_viewer/lib/id_utils.js';
import { get as getIntercomTrackerStore } from 'zoomforth_zf/appglobals/intercom-tracker.js';

import { showErrorNotification } from './misc.js';

import { 
  generateMenuItemId,
  generateSectionId
} from 'zoomforth_codesbane_editor/lib/id_generators.js';

import {
  showModal
} from './editor_modals.js';

import ConfirmDeleteSubpageModal
  from 'zoomforth_codesbane_editor/exterior_editor/editor_modals/confirm_delete_subpage_container.jsx';

import {
  generateSubpageId,
  makeNewSubpageData
} from 'zoomforth_codesbane_viewer/lib/subpages.js';

import {
  makeNewMenuItemData
} from 'zoomforth_codesbane_viewer/lib/navigation.js';

import {
  getSubpageById,
  getOrderedMainMenuItems,
  getOrderedSubmenuItemsForSubpage,
  getSerializableSubpageData,
  getCurrentSubpage
} from 'zoomforth_codesbane_viewer/reducers/index.js';

import {
  getPageId
} from 'zoomforth_codesbane_editor/reducers/index.js';

import {
  deleteSection,
  createSectionFromSerializableSectionAndContent,
  createSection,
  createTabsSectionWithNestedSections
} from './sections.js';

import {
  createSectionBackgroundImageContent,
  createSectionBackgroundVideoContent
} from './content.js';

import {
  isVideoishAssetType
} from 'zoomforth_codesbane_viewer/lib/page_content_utils.js';

import {
  setClipboardData,
  fetchClipboardDataForType
} from './user_clipboard.js';

import {
  ensureThrottledPageConfigSaving
} from './config.js';

import {
  createMenuItem,
  editExistingMenuItem
} from './navigation.js';

import {
  setCurrentSubpageId
} from 'zoomforth_codesbane_viewer/action_creators/viewer.js';

import {
  CLIPBOARD_ITEM_TYPE_SUBPAGE
} from 'zoomforth_codesbane_editor/lib/user_clipboard/const.js';


export const confirmDeleteSubpage = (subpageOrId)=>
  (dispatch, getState)=> {
    const subpageId = getIdFrom(subpageOrId);
    const subpage = getSubpageById(getState(), subpageId);

    const subpageMenuItem = find(
      getOrderedMainMenuItems(getState()),
      ({ appAction })=>
        appAction.subpageId === subpageId
    );

    dispatch(showModal(ConfirmDeleteSubpageModal, { subpage, subpageTitle:subpageMenuItem.title }));

  };


export const deleteSubpage = (subpageOrId)=>
  (dispatch, getState)=> {
    const subpageIdToDelete = getIdFrom(subpageOrId);

    const state = getState();
    
    // Delete all menu items which point at this subpage
    const orderedMainMenuItems = getOrderedMainMenuItems(state, state);

    const menuItemsToDelete = filter(
      [ ...orderedMainMenuItems, ...getOrderedSubmenuItemsForSubpage(state, subpageIdToDelete)],
      ({ appAction })=> appAction && appAction.subpageId === subpageIdToDelete
    );
    dispatch({
      type: 'DELETE_MENU_ITEMS',
      menuItemIds: map(menuItemsToDelete, 'id')
    });

    const subpage = getSubpageById(state, subpageIdToDelete);
    const sectionIdsToDelete = subpage.orderedSectionIds;

    each(sectionIdsToDelete, sectionId => {
      dispatch(deleteSection(sectionId));
    });


    const nextUnhiddenMenuItem = find(
      orderedMainMenuItems, 
      menuItem => {
        const menuSubpageId = get(menuItem, 'appAction.subpageId');
        if (menuSubpageId === subpageIdToDelete) {
          return false;
        }
        const subpage = getSubpageById(state, menuSubpageId);
        return subpage && !subpage.isHidden;
      }
    );

    if (getCurrentSubpage(state).id === subpageIdToDelete) {
      dispatch(setCurrentSubpageId(nextUnhiddenMenuItem.appAction.subpageId));
    }

    // Delete this subpage
    dispatch({
      type: 'DELETE_SUBPAGE',
      subpageIdToDelete
    });

    dispatch(ensureThrottledPageConfigSaving());
  };


export const setSubpageData = (subpageOrId, data)=>
  (dispatch)=> {
    const subpageId = getIdFrom(subpageOrId);
    dispatch({
      type: 'SET_SUBPAGE_DATA',
      subpageId,
      data
    });
  };


/**
 * Create a new subpage from serializableSubpageData. This will
 * recreate the subpage object and create new sections and content for those
 * sections.
 *
 * This serializableSubpageData can be generated via the
 * getSerializableSubpageData Redux selector.
 */
export const createSubpageFromSerializableSubpage = (serializableSubpageData)=>
  (dispatch)=> {

    const { originalSubpageData, serializableSections } = serializableSubpageData;
    const { displayName, ...restSubpageData } = originalSubpageData;

    const newSubpageId = generateSubpageId();

    const newSubpage = {
      ...restSubpageData,
      ...makeNewSubpageData({ id: newSubpageId })
    };

    const newSubpageMenuItemId = generateMenuItemId();
    const newSubpageMenuItem = makeNewMenuItemData({
      id: newSubpageMenuItemId,
      title: `Copy of ${displayName || 'Subpage'}`,
      menuItemType: LINK_TYPE_INTERNAL, 
      isPrimaryForSubpage: true, 
      parentSubpageId: newSubpage.id
    });

    // save the actual subpage data
    dispatch(setSubpageData(newSubpageId, newSubpage));

    // create a menu item for the subpage
    dispatch(createMenuItem(newSubpageMenuItem));

    // switch to the subpage
    dispatch(setCurrentSubpageId(newSubpageId));

    // rebuild each section of the subpage
    each(serializableSections, (serializableSection, idx)=> {
      const { originalSectionData, nestedSectionsData, backgroundAsset, fallbackBackgroundAsset } = serializableSection;
      const { originalSectionId } = originalSectionData;

      if (!nestedSectionsData || nestedSectionsData.length === 0) {
        dispatch(createSectionFromSerializableSectionAndContent(
          newSubpage,
          idx,
          serializableSection,
          originalSectionId,
          { shouldScrollAndEditSection: false }
        ));
      }
      else {
        createTabsSectionWithNestedSections({
          subpage: newSubpage,
          atIndex: idx,
          tab: null,
          originalSectionData,
          nestedSectionsData,
          backgroundAsset,
          fallbackBackgroundAsset,
          dispatch
        });
      }
    });

    dispatch(ensureThrottledPageConfigSaving());

    dispatch(editExistingMenuItem(newSubpageMenuItem));
  };


export const copySubpage = (subpageOrId)=>
  (dispatch, getState)=> {
    const serializableSubpageData = getSerializableSubpageData(getState(), subpageOrId);

    dispatch(setClipboardData(CLIPBOARD_ITEM_TYPE_SUBPAGE, serializableSubpageData));
    getIntercomTrackerStore().trackPageEditorCopySubpage(getPageId(getState()));
  };

export const pasteSubpage = ()=>
  (dispatch, getState)=>  {
    dispatch(fetchClipboardDataForType(CLIPBOARD_ITEM_TYPE_SUBPAGE, (serializableSubpageData)=> {
      if (!serializableSubpageData) {
        loglevel.error('No subpage data found to paste -- paste was enabled though');
        dispatch(showErrorNotification(
          'Clipboard Error',
          'Could not retrieve subpage data from your clipboard.'
        ));
        return;
      }

      dispatch(createSubpageFromSerializableSubpage(serializableSubpageData));

      getIntercomTrackerStore().trackPageEditorPasteSubpage(getPageId(getState()));
    }));
  };

export const pasteMultipleCopiedSubpagesDataIntoSite = (clipboardItems) =>
  (dispatch, getState)=>  {
    each(clipboardItems, subpage => {
      dispatch(createSubpageFromSerializableSubpage(subpage.data));
      getIntercomTrackerStore().trackPageEditorPasteSubpage(getPageId(getState()));
    });
  };

export const duplicateSubpage = (subpageOrId)=>
  (dispatch, getState)=> {
    const serializableSubpageData = getSerializableSubpageData(getState(), subpageOrId);
    dispatch(createSubpageFromSerializableSubpage(serializableSubpageData));
    getIntercomTrackerStore().trackPageEditorDuplicateSubpage(getPageId(getState()));
  };

export const setMainSectionForSubpage = (subpageOrId, sectionOrId) =>
  (dispatch)=> {
    const subpageId = getIdFrom(subpageOrId);
    const sectionId = getIdFrom(sectionOrId);

    dispatch(setSubpageData(subpageId, {
      mainSectionId: sectionId
    }));

    dispatch(ensureThrottledPageConfigSaving());
  };
